/**
 * QZ Tray Service
 * Handles communication with QZ Tray for receipt printing
 */

class QZTrayService {
  constructor() {
    this.isConnected = false;
    this.qz = null;
  }

  /**
   * Initialize QZ Tray connection
   */
  async initialize() {
    if (typeof window.qz === 'undefined') {
      throw new Error('QZ Tray library not loaded');
    }

    this.qz = window.qz;

    try {
      // Set up QZ Tray security certificates and signing
      await this.setupSecurity();
      
      await this.connect();
      return true;
    } catch (error) {
      console.error('Failed to initialize QZ Tray:', error);
      throw error;
    }
  }

  /**
   * Set up QZ Tray security (certificate and signing)
   * For production with proper certificate infrastructure
   */
  async setupSecurity() {
    console.log('[QZ Security] Checking if security is required...');
    
    // Check if we should use unsigned mode (for development/testing)
    // This allows QZ Tray to work without certificate validation
    const useUnsignedMode = process.env.REACT_APP_QZ_UNSIGNED === 'true';
    
    if (useUnsignedMode) {
      console.log('[QZ Security] Using unsigned mode (no certificate required)');
      // Set empty certificate promise - allows unsigned connections
      this.qz.security.setCertificatePromise((resolve) => {
        resolve(); // No certificate - QZ Tray will allow if configured
      });
      return;
    }
    
    console.log('[QZ Security] Setting up certificate and signature promises...');
    
    // IMPORTANT: Set signature algorithm to SHA512 to match backend signing
    // Must be called before setCertificatePromise and setSignaturePromise
    if (this.qz.security.setSignatureAlgorithm) {
      this.qz.security.setSignatureAlgorithm("SHA512");
      console.log('[QZ Security] Signature algorithm set to SHA512');
    }
    
    // Embedded certificate (works for all domains/white-labels)
    const EMBEDDED_CERTIFICATE = `-----BEGIN CERTIFICATE-----
MIIEGzCCAwOgAwIBAgIURfvsl1RrLLn8mDQZlGCemoxV550wDQYJKoZIhvcNAQEL
BQAwgZwxCzAJBgNVBAYTAlVTMQ0wCwYDVQQIDARPaGlvMRQwEgYDVQQHDAtIb2xt
ZXN2aWxsZTETMBEGA1UECgwKQ2FzdGxlIFBPUzERMA8GA1UECwwIUHJpbnRpbmcx
GjAYBgNVBAMMEWFwcC5jYXN0bGVwb3MuY29tMSQwIgYJKoZIhvcNAQkBFhVzdXBw
b3J0QGNhc3RsZXBvcy5jb20wHhcNMjYwMTEzMTczMjAwWhcNMzYwMTExMTczMjAw
WjCBnDELMAkGA1UEBhMCVVMxDTALBgNVBAgMBE9oaW8xFDASBgNVBAcMC0hvbG1l
c3ZpbGxlMRMwEQYDVQQKDApDYXN0bGUgUE9TMREwDwYDVQQLDAhQcmludGluZzEa
MBgGA1UEAwwRYXBwLmNhc3RsZXBvcy5jb20xJDAiBgkqhkiG9w0BCQEWFXN1cHBv
cnRAY2FzdGxlcG9zLmNvbTCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoCggEB
AJVaEw50OBf9iqDgDDjgGnX8VVbfpZxU9a8X+kHElJRiYZz9eXxuoZE129fbdjiS
QStMKtWRcfQsl35mTdGjhDQPTwpANC6ps/GtzXJJrCgY84yLNXRRHpaVeZmpTnqF
0xOItMGyuJxlXO5wCPkfLtb0mhh0I6Qh+Xe9dtZSp2hFFSMdBJ6pTKARJlsf7khE
8s4rCcGlGqAZx8lxdmrvqEESCJfw3i50QtLbSPNdVNg3NzySan4LkZfM89cI77kH
NGRk9m0K7K+Ho4goHOlcVyO7DDRdtfrFnvdoqdKzB7do0b4n4FNCFQwvQHWCVEhU
fqrO9KSNDpy8MnU07BATWg8CAwEAAaNTMFEwHQYDVR0OBBYEFK5BCETZlfhPltkp
HdAzsC62zuAuMB8GA1UdIwQYMBaAFK5BCETZlfhPltkpHdAzsC62zuAuMA8GA1Ud
EwEB/wQFMAMBAf8wDQYJKoZIhvcNAQELBQADggEBAGnXdR7pomAPN0hak0mzQony
nIinkiyZmoiEXKQroL1W6uejI/Ka3iGqmJROvRQMXm0NlHuPjgyLyvFJoUTYBPit
Q2IIxeb3veaJJs/ILz/GaE/dMX6RXYHvta/1OnSqmLFODXxHENT5ilFAkqsbndkb
QDFTJc0NQbvGsLTehRpmI54CP+ROiXLvsnSe41J4QOfo70Z6FV7ZFbr5u09L1l1N
5dkXE54NkWkc4N7huMslVT1JhUggebJcd7t50GA0E/RHDeJOFaDYvoiVxb4+gnM+
VOpXn+8wxVERgS5H3g+yHK3udzlzEp8Szn68ujhMNX6e7rM8+rSiPMVFbwGVnx4=
-----END CERTIFICATE-----`;
    
    // Set certificate promise - use embedded certificate
    this.qz.security.setCertificatePromise((resolve, reject) => {
      console.log('[QZ Security] Using embedded certificate');
      resolve(EMBEDDED_CERTIFICATE);
    });

    // Set signature promise - backend signs data with private key
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    
    this.qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        console.log('[QZ Security] Requesting signature from backend:', backendUrl);
        fetch(`${backendUrl}/api/qz/sign`, {
          method: 'POST',
          body: toSign,
          headers: {
            'Content-Type': 'text/plain'
          }
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(`Signature request failed: ${response.status}`);
            }
            return response.text();
          })
          .then(signature => {
            console.log('[QZ Security] Signature received successfully');
            resolve(signature);
          })
          .catch(error => {
            console.error('[QZ Security] Signature request failed:', error);
            reject(error);
          });
      };
    });
    
    console.log('[QZ Security] Security hooks configured with embedded certificate');
  }

  /**
   * Detect if running on Android
   */
  isAndroidPlatform() {
    return typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
  }

  /**
   * Connect to QZ Tray
   * Supports both desktop and Android (QZ Tray Android service)
   */
  async connect() {
    // Check if QZ Tray itself reports as active (most reliable check)
    if (this.qz && this.qz.websocket && this.qz.websocket.isActive()) {
      console.log('QZ Tray already connected (checked via QZ websocket)');
      this.isConnected = true;
      return true;
    }
    
    // Also check our internal flag
    if (this.isConnected) {
      console.log('QZ Tray already connected (internal flag)');
      return true;
    }

    try {
      // Ensure security hooks are set up before connecting
      if (!this.qz.security.getCertificatePromise()) {
        console.log('[QZ Connect] Security hooks not set, setting up now...');
        await this.setupSecurity();
      }
      
      const isAndroid = this.isAndroidPlatform();
      
      // Android-specific configuration
      const config = isAndroid ? {
        host: "localhost",
        port: 8181,
        retries: 10,
        delay: 1.2  // Exponential backoff up to 1.5 seconds
      } : {
        retries: 5,
        delay: 1
      };
      
      console.log(`Connecting to QZ Tray (${isAndroid ? 'Android' : 'Desktop'})...`, config);
      
      await this.qz.websocket.connect(config);
      this.isConnected = true;
      console.log('Connected to QZ Tray successfully');
      return true;
    } catch (error) {
      console.error('QZ Tray connection failed:', error);
      
      // Check if the error is "already connected" - treat as success
      if (error.message && error.message.includes('already exists')) {
        console.log('QZ Tray was already connected - treating as success');
        this.isConnected = true;
        return true;
      }
      
      const isAndroid = this.isAndroidPlatform();
      const errorMessage = isAndroid 
        ? 'QZ Tray Android is not running. Please ensure QZ Tray Android service is installed and running.'
        : 'QZ Tray is not running. Please ensure QZ Tray is installed and running on your computer.';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Disconnect from QZ Tray
   */
  async disconnect() {
    if (this.isConnected && this.qz) {
      try {
        await this.qz.websocket.disconnect();
        this.isConnected = false;
        console.log('Disconnected from QZ Tray');
      } catch (error) {
        console.error('Error disconnecting from QZ Tray:', error);
      }
    }
  }

  /**
   * Get list of available printers
   */
  async listPrinters() {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      const printers = await this.qz.printers.find();
      console.log('Available printers:', printers);
      return Array.isArray(printers) ? printers : [printers];
    } catch (error) {
      console.error('Failed to list printers:', error);
      throw error;
    }
  }

  /**
   * Get default printer
   */
  async getDefaultPrinter() {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      const printer = await this.qz.printers.getDefault();
      console.log('Default printer:', printer);
      return printer;
    } catch (error) {
      console.error('Failed to get default printer:', error);
      throw error;
    }
  }

  /**
   * Print receipt (wrapper for unified API)
   * @param {string} html - HTML content to print
   * @param {object} options - Print options (printerName, margins, etc.)
   */
  async printReceipt(html, options = {}) {
    return await this.printHTMLReceipt(html, options.printerName, options.margins);
  }

  /**
   * Print HTML receipt
   * @param {string} htmlContent - HTML content to print
   * @param {string} printerName - Optional printer name (uses default if not specified)
   * @param {object} margins - Optional margins {top, left, right, bottom} in mm
   */
  async printHTMLReceipt(htmlContent, printerName = null, margins = null) {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      // Get printer name if not specified
      if (!printerName) {
        printerName = await this.getDefaultPrinter();
      }

      console.log('Printing to:', printerName);
      
      // Margins are handled in the HTML/CSS, not by QZ Tray
      // Set printer margins to 0 to avoid double-margins
      console.log('Margins handled by HTML/CSS, QZ Tray margins set to 0');

      // Create printer configuration for thermal receipt printer (80mm / 3.15")
      const config = this.qz.configs.create(printerName, {
        size: { width: 3.15, height: 0 }, // 80mm width (3.15 inches), auto height
        units: 'in',
        scaleContent: false, // Don't scale - let HTML handle sizing
        rasterize: true, // Crucial for thermal receipt printers receiving HTML
        jobName: 'Castle POS Receipt',
        margins: { top: 0, right: 0, bottom: 0, left: 0 }
      });

      // Add bottom spacing by appending blank lines to HTML
      const bottomMargin = margins?.bottom || 0;
      let modifiedHtml = htmlContent;
      
      if (bottomMargin > 0) {
        // Add blank lines before closing body tag
        const numLines = Math.round(bottomMargin / 4); // ~4mm per line
        const blankLines = '<div style="line-height: 4mm;">' + '&nbsp;<br/>'.repeat(numLines) + '</div>';
        modifiedHtml = htmlContent.replace('</body>', blankLines + '</body>');
        console.log(`Bottom margin: ${bottomMargin}mm, adding ${numLines} blank lines`);
      }

      // QZ Tray pixel/html format treats `data` as a URL to fetch.
      // Wrap in a data URI so QZ renders inline HTML correctly.
      const htmlDataUri = 'data:text/html;charset=utf-8,' + encodeURIComponent(modifiedHtml);
      
      // Prepare data for printing
      const data = [{
        type: 'pixel',
        format: 'html',
        data: htmlDataUri
      }];

      // Print
      await this.qz.print(config, data);
      console.log('Receipt printed successfully');
      return true;
    } catch (error) {
      console.error('Print job failed:', error);
      throw new Error(`Failed to print receipt: ${error.message}`);
    }
  }

  /**
   * Print raw ESC/POS commands (for thermal printers)
   * @param {string} escposData - ESC/POS command string
   * @param {string} printerName - Optional printer name
   */
  async printRawReceipt(escposData, printerName = null) {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      if (!printerName) {
        printerName = await this.getDefaultPrinter();
      }

      console.log('Printing raw data to:', printerName);

      const config = this.qz.configs.create(printerName);
      const data = [{
        type: 'raw',
        format: 'command',
        data: escposData
      }];

      await this.qz.print(config, data);
      console.log('Receipt printed successfully (raw)');
      return true;
    } catch (error) {
      console.error('Raw print job failed:', error);
      throw new Error(`Failed to print receipt: ${error.message}`);
    }
  }

  /**
   * Open cash drawer via ESC/POS command
   * Sends drawer kick pulse to pin 2 (standard for most cash drawers)
   * @param {string} printerName - Optional printer name
   */
  async openCashDrawer(printerName = null) {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      if (!printerName) {
        printerName = await this.getDefaultPrinter();
      }

      console.log('Opening cash drawer via:', printerName);

      // ESC/POS command to open cash drawer on pin 2
      // ESC p m t1 t2 (1B 70 00 19 FA)
      const drawerKickCommand = '\x1B\x70\x00\x19\xFA';

      const config = this.qz.configs.create(printerName);
      const data = [{
        type: 'raw',
        format: 'command',
        data: drawerKickCommand
      }];

      await this.qz.print(config, data);
      console.log('Cash drawer kick command sent successfully');
      return true;
    } catch (error) {
      console.error('Cash drawer kick failed:', error);
      throw new Error(`Failed to open cash drawer: ${error.message}`);
    }
  }

  /**
   * Check if QZ Tray is available
   */
  isAvailable() {
    return typeof window.qz !== 'undefined';
  }

  /**
   * Check if QZ Tray is actively running (Android or Desktop)
   */
  async isActive() {
    if (!this.isAvailable()) {
      return false;
    }
    
    try {
      return await this.qz.websocket.isActive();
    } catch (error) {
      return false;
    }
  }

  /**
   * Launch QZ Tray Android app (if installed)
   * Only works on Android platform
   */
  launchQZTrayAndroid() {
    if (!this.isAndroidPlatform()) {
      console.warn('Cannot launch QZ Tray Android on non-Android platform');
      return false;
    }
    
    try {
      // Try to launch QZ Tray Android using custom URI scheme
      window.location.href = "qz:tray";
      return true;
    } catch (error) {
      console.error('Failed to launch QZ Tray Android:', error);
      return false;
    }
  }

  /**
   * Test print - sends a simple test receipt
   * @param {string} printerName - Printer to test
   */
  async testPrint(printerName = null) {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      if (!printerName) {
        printerName = await this.getDefaultPrinter();
      }

      console.log('Sending test print to:', printerName);

      // Simple HTML test receipt
      const testHTML = `
        <html>
          <head>
            <style>
              body { font-family: monospace; text-align: center; margin: 0; padding: 10px; }
              h1 { font-size: 24px; margin: 10px 0; }
              p { font-size: 14px; margin: 5px 0; }
              .line { border-top: 1px dashed #000; margin: 10px 0; }
            </style>
          </head>
          <body>
            <h1>TEST PRINT</h1>
            <p>Castle POS</p>
            <div class="line"></div>
            <p>Printer: ${printerName}</p>
            <p>Time: ${new Date().toLocaleString()}</p>
            <div class="line"></div>
            <p>✓ Printer Connection OK</p>
          </body>
        </html>
      `;

      await this.printHTMLReceipt(testHTML, printerName);
      console.log('Test print completed successfully');
      return true;
    } catch (error) {
      console.error('Test print failed:', error);
      throw error;
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Get environment info for unified service
   */
  getEnvironmentInfo() {
    return {
      platform: 'web',
      isNative: false,
      service: 'QZ Tray'
    };
  }
}

// Export singleton instance
const qzTrayService = new QZTrayService();
export default qzTrayService;
